/**
 * WebsiteApp: a sandboxed, sales-oriented clone of the multi-page
 * ConfirmPH Mini-Site that Enterprise businesses get automatically
 * (route: `/rentals/:slug`, layout: MiniSiteLayout).
 *
 * Purpose is to let a promoter tell an owner:
 *   "This is the professional rental website you get out of the box on
 *    ConfirmPH: no web developer, no separate domain, no CMS.  A visitor
 *    can browse your fleet, check availability, and send a booking
 *    request, and it all lands in your Booking Intake."
 *
 * Renders sticky-nav Home / Fleet / Booking / About / Policies / FAQ /
 * Contact tabs to visually match the real Mini-Site's IA.
 */

import { useState } from "react";
import {
  Car, Calendar, Phone, Mail, MapPin, ShieldCheck, Star, Sparkles,
  CheckCircle2, ChevronDown, ChevronRight,
} from "lucide-react";
import { DemoBadge } from "../DemoPanels";

const TABS = [
  { id: "home",     label: "Home" },
  { id: "fleet",    label: "Fleet" },
  { id: "booking",  label: "Booking" },
  { id: "about",    label: "About" },
  { id: "policies", label: "Policies" },
  { id: "faq",      label: "FAQ" },
  { id: "reviews",  label: "Reviews" },
  { id: "contact",  label: "Contact" },
];

const fmt = (n) => "₱" + Number(n || 0).toLocaleString("en-PH");

export default function WebsiteApp({ website, onOpenBookingPage }) {
  const [tab, setTab] = useState("home");
  return (
    <div data-testid="website-app"
         className="min-h-full bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Sticky brand nav: mirrors MiniSiteLayout */}
      <header className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="px-4 sm:px-6 py-3 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-700 grid place-items-center text-white font-bold text-sm">
              {website.name.charAt(0)}
            </div>
            <div className="leading-tight">
              <div className="font-display font-semibold text-slate-900">{website.name}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
                Powered by ConfirmPH
              </div>
            </div>
          </div>
          <div className="flex-1" />
          <DemoBadge>PREVIEW</DemoBadge>
        </div>
        {/* Nav tabs */}
        <nav className="px-2 sm:px-4 pb-1 overflow-x-auto scrollbar-hide"
             data-testid="website-nav">
          <div className="flex items-center gap-0.5">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)}
                      data-testid={`website-tab-${t.id}`}
                      className={`px-3 py-2 rounded-t-md text-xs font-medium whitespace-nowrap transition-colors ${
                        tab === t.id ? "bg-emerald-50 text-emerald-800 border-b-2 border-emerald-600"
                                      : "text-slate-600 hover:text-slate-900"
                      }`}>
                {t.label}
              </button>
            ))}
          </div>
        </nav>
      </header>

      <div className="px-4 sm:px-6 py-5" data-testid={`website-body-${tab}`}>
        {tab === "home"     && <Home website={website} onCta={() => setTab("fleet")} />}
        {tab === "fleet"    && <Fleet website={website} onPick={() => setTab("booking")} />}
        {tab === "booking"  && <Booking website={website} onOpenBookingPage={onOpenBookingPage} />}
        {tab === "about"    && <About website={website} />}
        {tab === "policies" && <Policies website={website} />}
        {tab === "faq"      && <FAQ website={website} />}
        {tab === "reviews"  && <Reviews website={website} />}
        {tab === "contact"  && <Contact website={website} />}
      </div>

      <footer className="border-t border-slate-200 bg-slate-50 px-4 sm:px-6 py-4 text-[11px] text-slate-500">
        © {new Date().getFullYear()} {website.name}. Site by <span className="font-semibold text-emerald-800">ConfirmPH</span>.
      </footer>
    </div>
  );
}

/* ---------- Sections ---------- */

function Home({ website, onCta }) {
  return (
    <div className="space-y-6" data-testid="website-home">
      <section className="rounded-2xl bg-gradient-to-br from-emerald-900 via-emerald-800 to-slate-900 text-white p-6 sm:p-10 relative overflow-hidden">
        <div className="max-w-2xl">
          <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-300 font-semibold">
            {website.location}
          </div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold leading-tight">
            {website.hero_title}
          </h1>
          <p className="mt-3 text-sm sm:text-base text-emerald-100 max-w-md">
            {website.hero_sub}
          </p>
          <div className="mt-5 flex items-center gap-2 flex-wrap">
            <button onClick={onCta}
                    data-testid="website-hero-cta"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white text-emerald-900 text-sm font-semibold">
              Browse the fleet <ChevronRight className="w-4 h-4" />
            </button>
            <a href="#contact" onClick={(e) => e.preventDefault()}
               className="text-sm text-emerald-100 underline underline-offset-2">
              Or reach us directly
            </a>
          </div>
        </div>
      </section>

      <div className="grid sm:grid-cols-3 gap-3">
        <TrustCard icon={ShieldCheck}
                    title="Listed on ConfirmPH"
                    body="Documents uploaded and rental records active on ConfirmPH. ConfirmPH does not certify the operator." />
        <TrustCard icon={Star}
                    title={`${website.rating} · ${website.review_count} booking-linked reviews`}
                    body="Reviews from renters who completed a real ConfirmPH booking." />
        <TrustCard icon={Sparkles}
                    title="Clear booking confirmation"
                    body="Send a request from any page: you get a confirmation link once the business approves it." />
      </div>
    </div>
  );
}

function TrustCard({ icon: Icon, title, body }) {
  return (
    <div className="rounded-xl border border-slate-200 p-4 bg-white">
      <Icon className="w-5 h-5 text-emerald-700" />
      <div className="mt-2 font-display font-semibold text-slate-900">{title}</div>
      <p className="mt-1 text-xs text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

function Fleet({ website, onPick }) {
  return (
    <div data-testid="website-fleet">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">
        Every vehicle in {website.name}&apos;s current fleet
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {website.vehicles.map((v) => (
          <button key={v.id} onClick={onPick}
                  data-testid={`website-fleet-${v.id}`}
                  className="text-left rounded-xl border border-slate-200 bg-white overflow-hidden hover:border-slate-300 transition-colors">
            <div className="aspect-[4/3] bg-slate-100">
              <img src={v.photo} alt={v.name} className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = "none"; }} />
            </div>
            <div className="p-3">
              <div className="flex items-center gap-2">
                <Car className="w-4 h-4 text-slate-500" />
                <div className="font-medium truncate">{v.name}</div>
              </div>
              <div className="mt-0.5 text-xs text-slate-500">{v.transmission} · {v.seats} seats</div>
              <div className="mt-2 text-sm">
                <strong>{fmt(v.rate)}</strong> <span className="text-slate-500 text-xs">/ day</span>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function Booking({ website, onOpenBookingPage }) {
  return (
    <div className="max-w-lg" data-testid="website-booking">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">
        Request a booking
      </div>
      <p className="text-sm text-slate-700">
        Send us the vehicle, dates, and your contact: {website.name} will confirm in ConfirmPH and email you a signed booking link.
      </p>
      <button onClick={onOpenBookingPage}
              data-testid="website-open-booking-page"
              className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-lg btn-brand font-medium">
        Open the booking form <ChevronRight className="w-4 h-4" />
      </button>
      <p className="mt-3 text-[11px] text-slate-500">
        Tip for the promoter: the &ldquo;Open the booking form&rdquo; button takes visitors into the same live booking form used on the free ConfirmPH Business Page: try the &ldquo;Business page&rdquo; demo to see the flow end to end.
      </p>
    </div>
  );
}

function About({ website }) {
  return (
    <div className="max-w-2xl space-y-3" data-testid="website-about">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">About us</div>
      <p className="text-sm text-slate-700 whitespace-pre-line">{website.about}</p>
      <div className="text-xs text-slate-600 flex items-center gap-2">
        <MapPin className="w-3.5 h-3.5" /> {website.location}
      </div>
    </div>
  );
}

function Policies({ website }) {
  return (
    <div className="space-y-3" data-testid="website-policies">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Booking policies</div>
      {website.policies.map((p) => (
        <div key={p.title} className="rounded-lg border border-slate-200 p-3 bg-white">
          <div className="font-medium text-sm text-slate-900">{p.title}</div>
          <p className="mt-1 text-xs text-slate-600">{p.body}</p>
        </div>
      ))}
    </div>
  );
}

function FAQ({ website }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="space-y-2 max-w-2xl" data-testid="website-faq">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">FAQ</div>
      {website.faq.map((q, i) => (
        <div key={i} className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <button onClick={() => setOpen(open === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left">
            <span className="text-sm font-medium">{q.q}</span>
            <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${open === i ? "rotate-180" : ""}`} />
          </button>
          {open === i && (
            <div className="px-3 pb-3 text-xs text-slate-600">{q.a}</div>
          )}
        </div>
      ))}
    </div>
  );
}

function Reviews({ website }) {
  return (
    <div className="space-y-3 max-w-2xl" data-testid="website-reviews">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
        {website.rating} out of 5 · {website.review_count} reviews
      </div>
      {website.reviews.map((r, i) => (
        <div key={i} className="rounded-lg border border-slate-200 p-3 bg-white">
          <div className="flex items-center justify-between">
            <div className="font-medium text-sm text-slate-900">{r.name}</div>
            <div className="text-xs text-amber-500">{"★".repeat(r.stars)}</div>
          </div>
          <p className="mt-1 text-xs text-slate-600">{r.body}</p>
          <div className="mt-1 text-[10px] text-slate-400">{r.when}</div>
        </div>
      ))}
    </div>
  );
}

function Contact({ website }) {
  return (
    <div className="max-w-md space-y-2" data-testid="website-contact">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">Contact</div>
      <ContactRow icon={Phone} label={website.phone} />
      <ContactRow icon={Mail}  label={website.email} />
      <ContactRow icon={MapPin} label={website.location} />
      <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 p-3 flex items-start gap-2">
        <CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5" />
        <div className="text-xs text-emerald-900">
          When a visitor uses any button on this site, the message lands directly in your ConfirmPH Booking Intake: no shared inbox, no lost DMs.
        </div>
      </div>
    </div>
  );
}

function ContactRow({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 text-sm text-slate-700">
      <Icon className="w-4 h-4 text-slate-500" /> {label}
    </div>
  );
}
